import type { Ref } from 'vue';

import { useQuery } from '@tanstack/vue-query';

import type { Batteries } from '~/types/equipment';

import { useCrudCreate, useCrudUpdate } from '~/shared/hooks/useCrudMutations';
import useCrudQuery from '~/shared/hooks/useCrudQuery';
import { resolveEquipmentConflictMessage } from '~/utils/equipmentErrors';

import batteriesService from '../services/batteriesService';

export function useBatteries(params: Ref<Batteries.GetBatteriesRequest>) {
  return useCrudQuery(params, 'batteries', batteriesService.list.bind(batteriesService));
}

export function useBatteryById(id: Ref<null | number>) {
  return useQuery({
    enabled: () => !!id.value,
    queryFn: () => {
      if (!id.value) throw new Error('ID é obrigatório');
      return batteriesService.getById(id.value);
    },
    queryKey: ['battery', id],
  });
}

export function useCreateBattery() {
  const handleError = (error: any) => {
    const conflictMessage = resolveEquipmentConflictMessage(error);
    if (conflictMessage) return conflictMessage;
    return error?.response?.data?.message || 'Erro ao cadastrar bateria';
  };

  return useCrudCreate(
    'batteries',
    'Bateria',
    batteriesService.create.bind(batteriesService),
    handleError,
  );
}

export function useUpdateBattery() {
  const handleError = (error: any) => {
    if (error?.response?.status === 404) {
      return 'Bateria não encontrada';
    }
    const conflictMessage = resolveEquipmentConflictMessage(error);
    if (conflictMessage) return conflictMessage;
    return error?.response?.data?.message || 'Erro ao atualizar bateria';
  };

  return useCrudUpdate(
    'batteries',
    'Bateria',
    batteriesService.update.bind(batteriesService),
    handleError,
  );
}
